import type { MentionNames } from "@hightouch/lib/llm/asset-comments/mention-tokens";

import type { CommentAuthor } from "./comment-item";
import { splitOnMentions } from "./split-on-mentions";

/**
 * How each author is described to the agent asked to act on their comment.
 *
 * The kind is in the prompt because it changes how the agent should read the
 * note: a teammate's is a request, a review agent's is a finding.
 */
const AUTHOR_KIND_LABELS: Record<CommentAuthor["kind"], string> = {
  user: "user",
  reviewAgent: "agent reviewer",
  agent: "Hightouch agent",
};

/**
 * A comment, phrased as an instruction to the ad's agent.
 *
 * Mentions render as the `@Name` a reader sees rather than as the raw
 * `@[Name](user:4821)` tokens the body stores — the ids mean nothing to the
 * agent, and the names are what the comment is actually about.
 */
export function buildCommentAgentPrompt({
  author,
  body,
  mentionNames,
}: {
  author: CommentAuthor;
  body: string;
  mentionNames: MentionNames;
}): string {
  const text = splitOnMentions(body, mentionNames)
    .map((segment) => segment.text)
    .join("")
    .trim();

  return `Address this comment from ${AUTHOR_KIND_LABELS[author.kind]} ${author.name}:\n${text}`;
}
